import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import { JOB_PORTAL_KEYS, NS } from '../../../../i18n/constants/keys';
import {
  ApplySection,
  DocumentChecklist,
  JobDescription,
  JobInfoCards,
  JobPostDialog,
  JobPostHeader,
  JobPostSkeleton,
  JobUnavailable,
  PrivacyNoticeContent,
  QualifyingQuestions,
  ShareSection,
  VideoSection,
} from './components';
import { useJobKeyInfo, useJobPostDetails, useQualifyingDialog } from './hooks';

export const JobPostDetails = () => {
  const { t } = useTranslation(NS.jobPortal);
  const details = useJobPostDetails();

  const keyInfo = useJobKeyInfo(
    details.jobPostData,
    details.calculateDaysLeft,
    details.formatUnixTimestamp,
  );

  const { isQualifyingNextDisabled, handleQualifyingDialogClose, handleCancelQualifying } =
    useQualifyingDialog({
      loading: details.loading,
      qualifyingAnswers: details.qualifyingAnswers,
      qualifyingQuestions: details.qualifyingQuestions,
      currentQualifyingStep: details.currentQualifyingStep,
      setCurrentQualifyingStep: details.setCurrentQualifyingStep,
      setIsQualifyingModalOpen: details.setIsQualifyingModalOpen,
      setIsQualifyingCancelDialogOpen: details.setIsQualifyingCancelDialogOpen,
    });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      <JobPostHeader
          jobTitle={details.jobPostData?.jobTitle}
          companyLogo={details.companyLogo}
          endDate={details.jobPostData?.endDate}
          contentLanguages={details.contentLanguages}
          contentLang={details.contentLang}
          onContentLangChange={details.setContentLang}
      />

      {details.loading ? (
        <JobPostSkeleton />
      ) : details.jobPostData && details.jobPostData.active ? (
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <Stack spacing={2}>
                <JobInfoCards keyInfo={keyInfo} />
                <JobDescription htmlTemplate={details.jobPostData.htmlTemplate} />
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper sx={{ position: { lg: 'sticky' }, top: { lg: 16 }, p: 2 }}>
                <VideoSection
                    handlePlayPause={details.handlePlayPause}
                    isPlaying={details.isPlaying}
                    jobPostData={details.jobPostData}
                    url={details.url}
                    videoRef={details.videoRef}
                />
                <ApplySection onApplyClick={() => details.setIsDialogOpen(true)} />
                <ShareSection
                    shareOnX={details.shareOnX}
                    shareOnWhatsApp={details.shareOnWhatsApp}
                    shareOnLinkedIn={details.shareOnLinkedIn}
                    copyToClipboard={() => void details.copyToClipboard()}
                    mailtoUrl={details.mailtoUrl}
                    jobPostData={details.jobPostData}
                    shareOnFacebook={details.shareOnFacebook}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      ) : (
        <JobUnavailable jobPostData={details.jobPostData} />
      )}

      <JobPostDialog
          isOpen={details.isDialogOpen}
          onClose={() => details.setIsDialogOpen(false)}
          title={t(JOB_PORTAL_KEYS.details.privacyNotice)}
          severity="success"
          actionsAlign="center"
          confirmLabel={t(JOB_PORTAL_KEYS.details.acceptAll)}
          cancelLabel={t(JOB_PORTAL_KEYS.details.declineAll)}
          onConfirm={details.handleApply}
      >
        <PrivacyNoticeContent companyName={details.companyName} ccaEnabled={details.ccaEnabled} />
      </JobPostDialog>

      <JobPostDialog
          isOpen={details.isQualifyingModalOpen}
          onClose={handleQualifyingDialogClose}
          title={t(JOB_PORTAL_KEYS.details.preQualFormTitle, { jobTitle: details.jobPostData?.jobTitle || '' })}
          severity="success"
          actionsAlign="center"
          confirmLabel={
            details.loading
              ? t(JOB_PORTAL_KEYS.details.submitting)
              : details.isFinalQualifyingStep
                ? t(JOB_PORTAL_KEYS.details.finish)
                : t(JOB_PORTAL_KEYS.details.next)
          }
          cancelLabel={
            details.currentQualifyingStep === 0
              ? t(JOB_PORTAL_KEYS.details.cancel)
              : t(JOB_PORTAL_KEYS.details.back)
          }
          onConfirm={() => void details.handleQualifyingComplete()}
          confirmDisabled={isQualifyingNextDisabled}
          disableOutsideClose
          maxWidth="sm"
      >
        <Box sx={{ py: 1 }}>
          <QualifyingQuestions
              questions={details.qualifyingQuestions}
              currentStep={details.currentQualifyingStep}
              onComplete={details.setQualifyingAnswers}
          />
        </Box>
      </JobPostDialog>

      <JobPostDialog
          isOpen={details.isQualifyingCancelDialogOpen}
          onClose={() => details.setIsQualifyingCancelDialogOpen(false)}
          title={t(JOB_PORTAL_KEYS.details.cancelConfirmTitle)}
          severity="warning"
          actionsAlign="end"
          confirmLabel={t(JOB_PORTAL_KEYS.details.cancelConfirmYes)}
          cancelLabel={t(JOB_PORTAL_KEYS.details.cancelConfirmNo)}
          onConfirm={handleCancelQualifying}
      >
        <Typography variant="body2" color="text.secondary">
          {t(JOB_PORTAL_KEYS.details.cancelConfirmMessage)}
        </Typography>
      </JobPostDialog>

      <JobPostDialog
          isOpen={details.isModalOpen}
          onClose={details.handleCloseModal}
          title={t(JOB_PORTAL_KEYS.details.uploadDocuments)}
          severity="success"
          actionsAlign="center"
          confirmLabel={
            details.loading
              ? t(JOB_PORTAL_KEYS.details.submitting)
              : t(JOB_PORTAL_KEYS.details.submitDocuments)
          }
          cancelLabel={t(JOB_PORTAL_KEYS.details.cancel)}
          onConfirm={() => void details.handleSubmit()}
          confirmDisabled={!details.canSubmit}
          maxWidth="lg"
      >
        <Stack spacing={2} sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(JOB_PORTAL_KEYS.details.uploadDocumentsMessage)}
          </Typography>
          <DocumentChecklist
              documentRequirements={details.jobPostData?.documentRequirements}
              configurations={details.jobPostData?.documentConfigurations}
              selectedFiles={details.selectedFiles}
              onFileSelect={details.handleFileSelect}
              onBatchFileSelect={details.handleBatchFileSelect}
              onFileRemoveAtIndex={details.handleFileRemoveAtIndex}
              disabled={details.loading}
          />
        </Stack>
      </JobPostDialog>
    </Box>
  );
};
